import { IPersonaRuleNode, PersonaRuleOperator, isPersonaRuleGroup } from "@/api_framework/api_modals/HomeTileConfig";

export const ALL_INCLUSIVE_PERSONA = "all_inclusive";

export type PersonaNode =
  | { type: "PERSONA"; name: string }
  | { type: "NOT"; operand: PersonaNode }
  | { type: "AND" | "OR" | "XOR"; operands: PersonaNode[] };

type BinaryOperator = "AND" | "OR" | "XOR";

type Token =
  | { kind: "PERSONA"; value: string }
  | { kind: "OPERATOR"; value: "AND" | "OR" | "XOR" | "NOT" }
  | { kind: "LPAREN" }
  | { kind: "RPAREN" };

export interface IPersonaValidationResult {
  isValid: boolean;
  error: string | null;
  canonical: string | null;
  personas: string[];
  rule: IPersonaRuleNode | null;
}

const KEYWORDS = new Set(["AND", "OR", "XOR", "NOT"]);
const PERSONA_PATTERN = /^[a-z_][a-z0-9_]*$/i;

class ParseError extends Error {}

const tokenize = (expression: string): Token[] => {
  const tokens: Token[] = [];
  let cursor = 0;

  while (cursor < expression.length) {
    const character = expression[cursor];

    if (/\s/.test(character)) {
      cursor += 1;
      continue;
    }
    if (character === "(") {
      tokens.push({ kind: "LPAREN" });
      cursor += 1;
      continue;
    }
    if (character === ")") {
      tokens.push({ kind: "RPAREN" });
      cursor += 1;
      continue;
    }

    const rest = expression.slice(cursor);
    const word = /^[A-Za-z_][A-Za-z0-9_]*/.exec(rest)?.[0];
    if (!word) {
      throw new ParseError(`Unexpected character “${character}”.`);
    }

    const upperCased = word.toUpperCase();
    if (KEYWORDS.has(upperCased)) {
      tokens.push({ kind: "OPERATOR", value: upperCased as "AND" | "OR" | "XOR" | "NOT" });
    } else if (PERSONA_PATTERN.test(word)) {
      const previous = tokens[tokens.length - 1];
      if (previous?.kind === "PERSONA") previous.value = `${previous.value} ${word}`;
      else tokens.push({ kind: "PERSONA", value: word });
    } else {
      throw new ParseError(`“${word}” is not a valid persona name.`);
    }
    cursor += word.length;
  }

  return tokens;
};

const parseTokens = (tokens: Token[]): PersonaNode => {
  let position = 0;

  const peek = (): Token | undefined => tokens[position];

  const expectOperand = (): PersonaNode => {
    const token = peek();
    if (!token) throw new ParseError("The expression is incomplete.");

    if (token.kind === "LPAREN") {
      position += 1;
      const node = parseOr();
      const closing = peek();
      if (!closing || closing.kind !== "RPAREN") {
        throw new ParseError("Unbalanced brackets in the expression.");
      }
      position += 1;
      return node;
    }
    if (token.kind === "OPERATOR" && token.value === "NOT") {
      position += 1;
      return { type: "NOT", operand: expectOperand() };
    }
    if (token.kind === "PERSONA") {
      position += 1;
      return { type: "PERSONA", name: token.value };
    }
    if (token.kind === "RPAREN") {
      throw new ParseError("Unbalanced brackets in the expression.");
    }
    throw new ParseError(`“${token.value}” needs a persona on both sides.`);
  };

  const parseBinary = (operator: BinaryOperator, parseNext: () => PersonaNode): PersonaNode => {
    const operands = [parseNext()];
    for (;;) {
      const token = peek();
      if (!token || token.kind !== "OPERATOR" || token.value !== operator) break;
      position += 1;
      operands.push(parseNext());
    }
    return operands.length === 1 ? operands[0] : { type: operator, operands };
  };

  const parseAnd = (): PersonaNode => parseBinary("AND", expectOperand);
  const parseXor = (): PersonaNode => parseBinary("XOR", parseAnd);
  function parseOr(): PersonaNode {
    return parseBinary("OR", parseXor);
  }

  const node = parseOr();
  const trailing = peek();
  if (trailing) {
    if (trailing.kind === "RPAREN") throw new ParseError("Unbalanced brackets in the expression.");
    if (trailing.kind === "PERSONA") {
      throw new ParseError(`“${trailing.value}” needs an operator before it.`);
    }
    throw new ParseError("The expression is incomplete.");
  }
  return node;
};

const normalizeSpacing = (value: string): string => value.trim().replace(/\s+/g, " ");

const resolveKnownSpelling = (node: PersonaNode, known: Map<string, string>): PersonaNode => {
  switch (node.type) {
    case "PERSONA":
      return { type: "PERSONA", name: known.get(node.name) ?? node.name };
    case "NOT":
      return { type: "NOT", operand: resolveKnownSpelling(node.operand, known) };
    default:
      return { type: node.type, operands: node.operands.map((operand) => resolveKnownSpelling(operand, known)) };
  }
};

const isComposite = (node: PersonaNode): boolean => node.type !== "PERSONA";

const toCanonical = (node: PersonaNode): string => {
  switch (node.type) {
    case "PERSONA":
      return node.name.toLowerCase();
    case "NOT":
      return `NOT ${isComposite(node.operand) ? `(${toCanonical(node.operand)})` : toCanonical(node.operand)}`;
    default:
      return node.operands
        .map((operand) => (isComposite(operand) ? `(${toCanonical(operand)})` : toCanonical(operand)))
        .sort()
        .join(` ${node.type} `);
  }
};

const collectPersonas = (node: PersonaNode, sink: Map<string, string>): void => {
  if (node.type === "PERSONA") {
    const key = node.name.toLowerCase();
    if (!sink.has(key)) sink.set(key, node.name);
  } else if (node.type === "NOT") collectPersonas(node.operand, sink);
  else node.operands.forEach((operand) => collectPersonas(operand, sink));
};

const invalid = (error: string): IPersonaValidationResult => ({
  isValid: false,
  error,
  canonical: null,
  personas: [],
  rule: null,
});

const findUnknownPersonaError = (personas: string[], knownPersonas: readonly string[]): string | null => {
  const known = new Set(knownPersonas);
  const unknown = personas.find((persona) => !known.has(persona));
  if (!unknown) return null;

  const caseMatch = knownPersonas.find((persona) => normalizeSpacing(persona).toLowerCase() === unknown.toLowerCase());
  return caseMatch ? `Use “${caseMatch}”, as spelled in the persona list.` : `“${unknown}” is not in the persona list.`;
};

export const validatePersonaCombination = (
  expression: string,
  knownPersonas?: readonly string[],
): IPersonaValidationResult => {
  if (!expression?.trim()) {
    return invalid("Persona combination is required.");
  }

  let ast: PersonaNode;
  try {
    ast = parseTokens(tokenize(expression));
  } catch (error) {
    return invalid(error instanceof ParseError ? error.message : "Enter a valid persona combination.");
  }

  const known = knownPersonas && [...knownPersonas, ALL_INCLUSIVE_PERSONA];

  if (known) {
    const knownBySpacing = new Map(known.map((persona) => [normalizeSpacing(persona), persona]));
    ast = resolveKnownSpelling(ast, knownBySpacing);
  }

  const personaMap = new Map<string, string>();
  collectPersonas(ast, personaMap);
  const personas = Array.from(personaMap.keys())
    .sort()
    .map((key) => personaMap.get(key) as string);

  if (known) {
    const mentioned: string[] = [];
    const collectAll = (node: PersonaNode): void => {
      if (node.type === "PERSONA") mentioned.push(node.name);
      else if (node.type === "NOT") collectAll(node.operand);
      else node.operands.forEach(collectAll);
    };
    collectAll(ast);
    const unknownError = findUnknownPersonaError(mentioned, known);
    if (unknownError) return invalid(unknownError);
  }

  let rule: IPersonaRuleNode;
  try {
    rule = toPersonaRule(ast);
  } catch (error) {
    return invalid(error instanceof RuleConversionError ? error.message : "Enter a valid persona combination.");
  }

  return { isValid: true, error: null, canonical: toCanonical(ast), personas, rule };
};

class RuleConversionError extends Error {}

export const toPersonaRule = (node: PersonaNode): IPersonaRuleNode => {
  switch (node.type) {
    case "PERSONA":
      return { persona: node.name, value: true };
    case "NOT":
      if (node.operand.type !== "PERSONA") {
        throw new RuleConversionError("NOT applies to a single persona only, not to a bracketed group.");
      }
      return { persona: node.operand.name, value: false };
    case "XOR":
      throw new RuleConversionError("XOR cannot be saved. Rewrite the combination with AND, OR and NOT.");
    case "AND":
    case "OR":
      return {
        operator: node.type as PersonaRuleOperator,
        conditions: node.operands.map(toPersonaRule),
      };
  }
};

export const fromPersonaRule = (rule: IPersonaRuleNode | null | undefined): string => {
  if (!rule) return "";
  if (!isPersonaRuleGroup(rule)) {
    return rule.value === false ? `NOT ${rule.persona}` : rule.persona;
  }
  const parts = rule.conditions
    .map((condition) => {
      const rendered = fromPersonaRule(condition);
      if (!rendered) return "";
      return isPersonaRuleGroup(condition) && condition.conditions.length > 1 ? `(${rendered})` : rendered;
    })
    .filter(Boolean);
  return parts.join(` ${rule.operator} `);
};
